import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCountryPricingRuleUpdateMutation } from 'api';
import { Form, FormInstance, FormProps, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { getUpdatedPayload } from 'utils/helpers';
import { IEditPricing } from './index';

export type CountryPricingRuleType = {
  countryId: string;
  markup: number;
  minMargin: number;
  maxMargin: number;
};

export const useEditPricing = (props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const [updatePricingRules, { isLoading }] = useCountryPricingRuleUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const clearFieldValidation = useCallback((form: FormInstance, field: string) => {
    const hasError = form.getFieldError(field)?.length;
    if (hasError) {
      form.setFields([
        {
          name: field,
          errors: [], // Clears the error for filed
        },
      ]);
    }
  }, []);

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<CountryPricingRuleType>['onFinish'] = useCallback(
    async (values: CountryPricingRuleType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.countryId = id as string;
      try {
        const { data, error }: Record<string, any> = await updatePricingRules(payload);
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: 'Country pricing rules was successfully updated.',
          });
          onCancel();
        } else if (error?.data?.success === false) {
          Object.values(error?.data?.data || {}).forEach((message) => {
            openNotificationWithIcon({
              type: 'error',
              message: message as string,
            });
          });
        } else if (error?.data?.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      } catch (err) {
        console.error('Error updating country pricing rules:', err);
        openNotificationWithIcon({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },

    [updatePricingRules, openNotificationWithIcon, id, onCancel, dataToEdit]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [modalState, form, dataToEdit]);

  return {
    contextHolder,
    form,
    isLoading,
    clearFieldValidation,
    onFinish,
    onCancel,
  };
};
